import { useCallback, useState } from "react";
import api from "./api";
import { toast } from "../components/ui/sonner";
import { printInLightMode } from "./printInLightMode";
import { brandSlug } from "./brand";

// [Iter 326 — 2026-02-24] PWA PDF download loop fix.
//
// iOS Safari (and its PWA / "Add to Home Screen" mode) requires an
// active user-gesture token when triggering downloads via a
// programmatically-created `<a download>` element. The token expires
// the moment control returns from an `await` — so after we `await`
// the fetch of the PDF blob, the subsequent `a.click()` silently
// does nothing. The user sees the "Preparing…" toast finish, then
// nothing happens, then five minutes later they retry and get the
// same result. That's the "download loop".
//
// Detection is important: we only take the special path on iOS
// (Safari + PWA + WKWebView). Everywhere else, `a.click()` still
// works reliably and downloads directly.
function _isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPad on iOS 13+ reports as "MacIntel" with maxTouchPoints > 1.
  const isIPadOSSafari =
    /Mac/i.test(ua) && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;
  return /iPhone|iPad|iPod/i.test(ua) || isIPadOSSafari;
}

/** Attempt to save/open a blob URL in a way that survives on iOS PWA.
 *  Tries in order:
 *    1. Web Share API with the File (native iOS "Save to Files / Photos" sheet).
 *    2. `<a download>` click (works on desktop + Android).
 *    3. Persistent toast with a "Tap to open PDF" action button — user
 *       tap creates a fresh gesture that reliably opens the blob URL.
 */
async function _saveBlobPwaSafe({ blob, filename, blobUrl }) {
  const file = new File([blob], filename, { type: "application/pdf" });
  const iOS = _isIOS();

  // 1. Web Share API — iOS Safari lets us hand the file straight to
  //    "Save to Files" / "Save to Photos" / WhatsApp / Mail. This is
  //    THE canonical way to persist a generated file on iOS. It DOES
  //    require an active user gesture, so this only succeeds if the
  //    caller invoked us synchronously from a click handler AND the
  //    blob fetch didn't burn too much time. On iOS 17.4+ Apple
  //    relaxed the gesture rule slightly (accepts ~2s of gesture
  //    "quiet time"), which is why this now works in practice for
  //    small-to-medium PDFs.
  if (
    iOS
    && typeof navigator !== "undefined"
    && typeof navigator.canShare === "function"
    && navigator.canShare({ files: [file] })
    && typeof navigator.share === "function"
  ) {
    try {
      await navigator.share({
        files: [file],
        title: filename,
        // Intentionally no `text` — some iOS share targets treat
        // `text` as the message body and prepend it before the file,
        // which is not what a "Save PDF" flow wants.
      });
      return "shared";
    } catch (err) {
      if (err?.name === "AbortError") return "cancelled";
      // NotAllowedError / gesture-lost — fall through to next strategy.
      console.warn("[useServerPdf] navigator.share failed, falling back:", err);
    }
  }

  // 2. Classic <a download> click. This is the reliable path on
  //    desktop + Android + iOS Safari IF the gesture is still live.
  //    We try it unconditionally — on desktop it's a silent save; on
  //    iOS it's usually a no-op after `await` but doesn't hurt.
  try {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch (err) {
    console.warn("[useServerPdf] anchor click failed:", err);
  }

  // 3. For iOS PWA, ALWAYS surface a follow-up toast with a manual
  //    action button. This guarantees the user has a working save
  //    path even if steps 1 & 2 above silently did nothing. The
  //    button's onClick creates a FRESH user-gesture token, so the
  //    subsequent `window.open()` is honoured by Safari.
  if (iOS) {
    toast.message("PDF ready — tap to open", {
      description: "iOS won't auto-save. Tap 'Open PDF' to view or save via the share sheet.",
      duration: 20_000,
      action: {
        label: "Open PDF",
        onClick: () => {
          // Fresh gesture → this is allowed by iOS Safari.
          const w = window.open(blobUrl, "_blank");
          if (!w) {
            // Popup blocked — fall back to same-tab navigation which
            // always works on iOS. The browser renders the PDF and
            // the user can hit the share icon → "Save to Files".
            window.location.assign(blobUrl);
          }
        },
      },
    });
  }
  return "clicked";
}

/**
 * useServerPdf — Wave 19 #102.
 *
 * Reusable hook for "Download PDF" buttons across the app. Calls the
 * server-side Playwright endpoint, downloads the PDF as a blob, and
 * triggers the browser's save-as. Falls back to the in-browser print
 * (which still honours the Print Light Mode Contract) if the endpoint
 * times out, errors, or returns an HTTP failure.
 *
 * Usage:
 *   const { download, busy } = useServerPdf({ endpoint: `/jobcards/${id}/pdf`, filename: `JC_${shortId}.pdf` });
 *   <button onClick={download} disabled={busy}>{busy ? "Generating…" : "Download PDF"}</button>
 *
 * The `filename` field is just a hint for the browser save dialog —
 * the server's `Content-Disposition` header is the source of truth.
 */
export default function useServerPdf({
  endpoint,
  filename,
  fallbackPrint = true,
  // [Iter 346 — 2026-02-26] Bumped 90 s → 300 s. Dirk: "no timeout".
  // The backend now removes the photo cap entirely (see `_JC_PHOTO_CAP
  // = None` in `pdf_routes.py`), so a JC with 200 photos will render
  // every one. WeasyPrint takes ~50 ms per downscaled photo to lay out
  // — 200 photos ≈ 10 s render + ~5 s parallel R2 fetch, so 300 s is
  // 10× headroom for extreme cases. If the reverse proxy in front of
  // the app enforces a lower edge timeout the request will fail there
  // first; on Emergent's default deploy topology 300 s works.
  timeoutMs = 300_000,
  onSuccess,
  onError,
} = {}) {
  const [busy, setBusy] = useState(false);

  const download = useCallback(async () => {
    if (!endpoint) {
      if (fallbackPrint) printInLightMode();
      return;
    }
    setBusy(true);
    try {
      const res = await api.get(endpoint, {
        responseType: "blob",
        timeout: timeoutMs,
      });
      // Wave 19 #105 — integrity guard. Some production proxies return
      // an HTML/JSON error body with HTTP 200 (e.g. when an upstream
      // crashes during a streamed response). We MUST refuse to save a
      // non-PDF blob as application/pdf — that's what produces the
      // "Adobe could not open … damaged" experience for the user.
      const blob = res.data;
      const ct = (res.headers?.["content-type"] || res.headers?.get?.("content-type") || "").toLowerCase();
      if (!ct.includes("application/pdf") || !blob || blob.size < 100) {
        // Try to read a small JSON / text error body the server may have
        // returned and surface that to the toast.
        let detail = `Server returned ${ct || "unknown content"} (${blob?.size || 0} bytes)`;
        try {
          const txt = await blob.text();
          const j = JSON.parse(txt);
          if (j?.detail) detail = j.detail;
          else if (txt) detail = txt.slice(0, 200);
        } catch { /* not JSON, leave detail as-is */ }
        toast.error(`PDF generation failed: ${detail}`);
        onError?.(new Error(detail));
        return;
      }
      // Sniff the first 8 bytes to be certain — proxies sometimes lie
      // about the content-type. A real PDF starts with `%PDF-`.
      const head = new Uint8Array(await blob.slice(0, 8).arrayBuffer());
      const headStr = String.fromCharCode(...head);
      if (!headStr.startsWith("%PDF-")) {
        toast.error("PDF generation returned corrupted bytes — please retry.");
        onError?.(new Error("Bad PDF magic bytes: " + headStr));
        return;
      }
      const safeName = (filename || `${brandSlug()}_document.pdf`).replace(/[^A-Za-z0-9._-]+/g, "_");
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const url = URL.createObjectURL(pdfBlob);
      // [Iter 326] Route through the PWA-safe saver so iOS PWA doesn't
      // get stuck at "Preparing…". Keep the URL alive long enough for
      // the user to tap the "Open PDF" toast action (20s toast + margin).
      await _saveBlobPwaSafe({ blob: pdfBlob, filename: safeName, blobUrl: url });
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      onSuccess?.();
    } catch (e) {
      console.warn("Server PDF failed:", e);
      // [Iter 344 — 2026-02-25] Explicit timeout / abort handling.
      // Previously an axios ECONNABORTED reset the button with no
      // clear signal — user saw the toast disappear and thought
      // nothing happened. Now surface a real message + retry hint.
      const isTimeout = e?.code === "ECONNABORTED"
        || /timeout/i.test(e?.message || "");
      if (isTimeout) {
        toast.error(
          "PDF taking longer than expected — server is still working. "
          + "Wait 20 s and tap Download again; large JCs (20+ photos) "
          + "can take a full minute on the first render.",
          { duration: 15_000 },
        );
        onError?.(e);
        return;
      }
      // Try to extract the server's error detail from a blob-typed
      // response (axios with responseType:'blob' returns the error body
      // as a Blob too).
      let msg = e?.response?.data?.detail;
      if (!msg && e?.response?.data && typeof e.response.data.text === "function") {
        try {
          const txt = await e.response.data.text();
          try {
            const j = JSON.parse(txt);
            msg = j?.detail || msg;
          } catch { msg = txt.slice(0, 200) || msg; }
        } catch { /* noop */ }
      }
      if (msg) {
        toast.error(msg);
      }
      // [Iter 290zp.32] `fallbackPrint` defaults to `true` for staff /
      // internal pages, but ANY portal (customer-facing) caller MUST
      // pass `fallbackPrint: false` in their `useServerPdf({...})`
      // config. The fallback path here calls the browser's
      // `printInLightMode()` which produces a printout with the app
      // URL + page counter in header/footer — that leaked out as
      // "customer-facing PDF" and looked nothing like our WeasyPrint
      // template. If you're wiring a new PDF button on a portal
      // page, keep the fallback off.
      if (fallbackPrint && (!e?.response || e.response.status >= 500)) {
        toast.message("Falling back to browser print…");
        printInLightMode();
      } else if (!msg) {
        toast.error("PDF generation failed.");
      }
      onError?.(e);
    } finally {
      setBusy(false);
    }
  }, [endpoint, filename, fallbackPrint, timeoutMs, onSuccess, onError]);

  return { download, busy };
}
